import { findLastIndex, uniqBy } from 'lodash-es'
import { type KeyboardEvent, type MouseEvent, useRef, useState } from 'react'

import { Delay } from '@/components/ui/Delay'
import { FadeSlideAnimation } from '@/components/ui/FadeSlideAnimation'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll'
import { cn } from '../../lib/utils'
import { type ChatItem, isList, isParagraph } from '../../models/chat'
import type {
  ChatActionInterface,
  ChatActionWithValue,
  InsuranceApplication,
} from '../../models/insuranceApplication'
import { ChatAction } from './ChatAction'
import { ChatAttachmentChips } from './ChatAttachmentChips'
import { ChatBotAvatar } from './ChatBotAvatar'
import { ChatEditDialogs } from './ChatEditDialogs'
import { ChatMessageComponent } from './ChatMessageComponent'
import { TypingIndicator } from './TypingIndicator'

interface ChatProps {
  messages: ChatItem[]
  pendingMessages?: ChatItem[]
  action?: ChatActionInterface
  showActionByDefault?: boolean
  hideAskQuestions?: boolean
  showTypingDots?: boolean
  disableAutoScroll?: boolean
  sendMessage: (data: ChatActionWithValue | FormData) => void
  application?: InsuranceApplication
  onEditSuccess?: () => void
  orgId?: string
  /** clientId of the message currently streaming in, if any — its half-typed
   *  markdown gets patched up so it doesn't flicker through its own syntax. */
  streamingClientId?: string
}

export function Chat({
  messages,
  pendingMessages,
  action,
  showActionByDefault,
  hideAskQuestions,
  showTypingDots,
  disableAutoScroll,
  sendMessage,
  application,
  onEditSuccess,
  orgId,
  streamingClientId,
}: ChatProps) {
  const [editingItem, setEditingItem] = useState<ChatItem | null>(null)
  const now = useRef(new Date())
  const items = uniqBy([...(messages || []), ...(pendingMessages || [])], (item) => item.id || item.clientId)
  const chatRef = useRef<HTMLDivElement>(null)
  useChatAutoScroll({
    containerRef: chatRef,
    messageCount: items.length,
    actionKey: action?.content?.component,
    disabled: disableAutoScroll,
    deps: [pendingMessages?.length, messages?.length, action?.content?.component],
  })

  function shouldAnimateItem(item: ChatItem) {
    if (typeof item.content === 'object' && 'props' in item.content) {
      if (item.content.props?.disableEnteringAnimation) {
        return false
      }
      if (item.id && item.content.props?.disableServerEnteringAnimation) {
        return false
      }
    }
    if (!item.isFromUser && item.dateCreated && new Date(item.dateCreated) > now.current) {
      return true
    }
    return !item.id && isParagraph(item)
  }

  const canEdit = Boolean(application)
  const handleEdit = canEdit ? (item: ChatItem) => setEditingItem(item) : undefined
  const dotNumber = application?.business?.dotNumber

  // Avatar marks the start of a bot "turn" — the first bot message right
  // after a user reply (or the very first message in the chat).
  const startsTurn = (item: ChatItem, index: number) =>
    !item.isFromUser && (index === 0 || !!items[index - 1]?.isFromUser)
  const currentTurnStart = findLastIndex(items, startsTurn)

  function renderMessage(item: ChatItem, index: number) {
    const showAvatar = startsTurn(item, index)
    const avatarCurrent = index === currentTurnStart
    const streaming = Boolean(streamingClientId) && item.clientId === streamingClientId
    // Only the newest line comes in; the rest of a batch is already on screen.
    if (index === items.length - 1 && shouldAnimateItem(item)) {
      return (
        <div key={item.clientId || item.id}>
          <Delay visible={true} initiallyVisible={false} delay={1000}>
            <FadeSlideAnimation>
              <ChatMessage
                key={item.clientId || item.id}
                message={item}
                onEdit={handleEdit}
                orgId={orgId}
                dotNumber={dotNumber}
                showAvatar={showAvatar}
                avatarCurrent={avatarCurrent}
                streaming={streaming}
              />
            </FadeSlideAnimation>
          </Delay>
        </div>
      )
    }
    return (
      <ChatMessage
        key={item.clientId || item.id}
        message={item}
        onEdit={handleEdit}
        orgId={orgId}
        dotNumber={dotNumber}
        showAvatar={showAvatar}
        avatarCurrent={avatarCurrent}
        streaming={streaming}
      />
    )
  }

  return (
    <div ref={chatRef} className="flex flex-col">
      <div className="flex flex-col">
        {items?.map(renderMessage)}
        <TypingIndicator
          visible={
            Boolean(showTypingDots) ||
            (items.length > 0 &&
              !!items[items.length - 1]?.isFromUser &&
              (!action || (!!pendingMessages?.length && !showActionByDefault)))
          }
          withInitialDelay={!showTypingDots}
        />
      </div>

      {application && (
        <ChatEditDialogs
          application={application}
          editingItem={editingItem}
          setEditingItem={setEditingItem}
          onEditSuccess={onEditSuccess || (() => undefined)}
        />
      )}
      <ChatAction
        action={action}
        sendMessage={sendMessage}
        showActionByDefault={!pendingMessages?.length || !!showActionByDefault}
        pendingMessages={pendingMessages}
        hideAskQuestions={hideAskQuestions}
        orgId={orgId}
        dotNumber={dotNumber}
      />
    </div>
  )
}

/** Text, and a list the customer answered with (their commodities), sits in a bubble. */
function isBubble(item: ChatItem) {
  return isParagraph(item) || (item.isFromUser && isList(item))
}

/** A bubble sits on its speaker's side; ml-auto rather than self-end so it holds inside the fade wrapper too. */
function bubbleAlignment(item: ChatItem) {
  if (!isBubble(item)) return undefined
  return cn('w-fit max-w-[85%] wrap-anywhere', item.isFromUser && 'ml-auto')
}

interface ChatMessageProps {
  message: ChatItem
  onEdit?: (item: ChatItem) => void
  orgId?: string
  dotNumber?: string
  showAvatar?: boolean
  /** Only the newest turn keeps its avatar; older ones shrink away. */
  avatarCurrent?: boolean
  streaming?: boolean
}

function ChatMessage({
  message,
  onEdit,
  orgId,
  dotNumber,
  showAvatar,
  avatarCurrent,
  streaming,
}: ChatMessageProps) {
  const content = message.content
  const chatContent = typeof content === 'object' && 'component' in content ? content : null
  const attachments = chatContent?.attachments ?? []
  const innerContent = chatContent ? chatContent.content : content
  const hasText = typeof innerContent === 'string' ? innerContent.trim().length > 0 : Boolean(innerContent)
  const showEdit = Boolean(message.isEditable && onEdit)
  const paragraph = isBubble(message)
  const edit = () => onEdit!(message)
  const editProps = showEdit
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: (event: MouseEvent) => {
          // A link or chip inside the answer keeps its own click.
          if ((event.target as HTMLElement).closest('a, button')) return
          edit()
        },
        onKeyDown: (event: KeyboardEvent) => {
          if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
          event.preventDefault()
          edit()
        },
      }
    : {}
  const bubble = (
    <div
      data-chat-message
      {...editProps}
      className={cn(
        'rounded-xl',
        showEdit && 'cursor-pointer transition-opacity active:opacity-70',
        !showEdit && (paragraph ? 'mb-2' : 'mb-6'),
        paragraph
          ? cn('px-4 py-2.5', message.isFromUser ? 'bg-ink text-white' : 'bg-ink-100 text-ink')
          : 'font-medium',
        paragraph && !showEdit && bubbleAlignment(message)
      )}
    >
      <ChatMessageComponent
        content={message.content}
        handleSubmit={() => null}
        enableMarkdown={!message.isFromUser}
        orgId={orgId}
        dotNumber={dotNumber}
        streaming={streaming}
      />
      {attachments.length > 0 && (
        <div className={cn(hasText && 'mt-2')}>
          <ChatAttachmentChips attachments={attachments} />
        </div>
      )}
    </div>
  )

  return (
    <>
      {showAvatar && (
        // A grid row collapses to the avatar's height, so the space closes up as it shrinks.
        <div
          aria-hidden={!avatarCurrent}
          className={cn(
            'grid transition-all duration-500 ease-out motion-reduce:transition-none',
            avatarCurrent ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={cn(
                'origin-bottom-left transition-transform duration-500 ease-out motion-reduce:transition-none',
                !avatarCurrent && 'scale-50'
              )}
            >
              <ChatBotAvatar />
            </div>
          </div>
        </div>
      )}
      {showEdit ? (
        <div className={cn(paragraph ? 'mb-2' : 'mb-6', bubbleAlignment(message))}>
          <Tooltip>
            <TooltipTrigger asChild>{bubble}</TooltipTrigger>
            <TooltipContent>Edit answer</TooltipContent>
          </Tooltip>
        </div>
      ) : (
        bubble
      )}
    </>
  )
}
